import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {
	Text,
	TextField,
	NumberField,
	ComboBox,
	MultiCombobox,
	DatePicker,
	DateRangePicker,
	Checkbox,
} from '@forecast-it/design-system';
import {useIntl, type IntlShape} from 'react-intl';
import Util from '../../forecast-app/shared/util/util';
import type {FieldDescriptor, FieldOption} from './actionCardTypes';
import {useFieldOptions} from './useFieldOptions';

// We don't want the DS DateRangePicker's inline date inputs at all — they don't
// fit our 300px-ish Value cell and their `body/1` (16/24) font reads larger than
// the other small inputs. Instead we render a plain text value + a clickable
// calendar icon (the picker dialog still opens through the DS button). The
// wrapper hides every inner DS element except the calendar button and strips
// its border / control chrome so it sits cleanly next to our value text.
const DateRangeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-width: 0;
`;

const DateRangeValueText = styled.span`
	font-family: 'ABC Whyte', Inter, sans-serif;
	font-weight: 350;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
	color: #232334;
	flex: 1 0 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const CompactDateRangeWrapper = styled.div`
	flex: 0 0 auto;
	display: inline-flex;
	/* Strip the DS control wrapper border / background / gap — we only want
	   the calendar trigger button. */
	[class*='ControlWrapperstyled__StyledControlWrapper'] {
		border: none !important;
		background: transparent !important;
		padding: 0 !important;
		gap: 0 !important;
		height: auto !important;
		width: auto !important;
		outline: none !important;
	}
	/* Hide everything inside the control wrapper EXCEPT the calendar button
	   container. Targeting the FlexRow (start date + arrow + end date) and the
	   InfoDisplayWrapper helper labels via the structural selector below. */
	[class*='ControlWrapperstyled__StyledControlWrapper'] > div:first-child {
		display: none;
	}
	/* Strip the calendar button's left border so it doesn't look like an inline
	   form control anymore — it should read as a standalone icon. */
	[class*='InnerButton__LeftBorderWrapper'] {
		border: none !important;
		padding: 0 !important;
	}
`;

// Format a {start, end} pair into "yyyy-mm-dd — yyyy-mm-dd". If only one side
// is set we show that side and an em-dash for the other; if both are empty we
// return null so the parent can render its own placeholder.
const formatDateRange = (value: unknown): string | null => {
	if (!value || typeof value !== 'object' || !('start' in (value as object))) return null;
	const {start, end} = value as {start?: string; end?: string};
	if (!start && !end) return null;
	return `${start || '—'} — ${end || '—'}`;
};

type Props = {
	descriptor: FieldDescriptor;
	value: unknown;
	onCommit: (value: unknown) => void;
	errorKey?: string | null;
	readOnly?: boolean;
};

const formatReadOnly = (value: unknown): string => {
	if (value !== null && value !== undefined && typeof value === 'object' && 'start' in (value as object)) {
		const {start, end} = value as {start?: string; end?: string};
		return `${start ?? ''} - ${end ?? ''}`;
	}
	if (Array.isArray(value)) return value.join(', ');
	return String(value ?? '');
};

type CommitTextFieldProps = {
	value: string;
	onCommit: (v: string) => void;
	hasError: boolean;
};

const CommitTextField = ({value, onCommit, hasError}: CommitTextFieldProps): JSX.Element => {
	const [local, setLocal] = useState(value);
	const wrapperRef = useRef<HTMLDivElement>(null);
	// Resync local state when the parent pushes a new value (e.g. after save/reset).
	useEffect(() => {
		setLocal(value);
	}, [value]);
	// TextField does not forward onKeyDown — wrap in a div so keydown bubbles up.
	// On Enter, blur the active input so exactly one commit fires (via onBlur); prevent
	// the default so Enter doesn't commit directly AND then blur-commit a second time.
	return (
		<div
			ref={wrapperRef}
			onKeyDown={(e: React.KeyboardEvent) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					(wrapperRef.current?.querySelector('input') as HTMLInputElement | null)?.blur();
				}
			}}
		>
			<TextField
				value={local}
				onValueChange={setLocal}
				onBlur={() => onCommit(local)}
				hasError={hasError}
				size={TextField.SIZE.SMALL}
				width={TextField.WIDTH.FLEX}
			/>
		</div>
	);
};

type CommitNumberFieldProps = {
	value: number;
	onCommit: (v: number) => void;
	hasError: boolean;
};

/**
 * NumberField does not expose onBlur in its public typings. Use `onInputChange`
 * (a public DS prop fired on blur and Enter) to trigger the commit.
 * React-aria consumes the Enter keydown event before it can bubble, so
 * the div-wrapper keydown approach used for CommitTextField does not work here.
 * A ref mirrors the latest numeric value so the commit callback is never stale.
 */
const CommitNumberField = ({value, onCommit, hasError}: CommitNumberFieldProps): JSX.Element => {
	const [local, setLocal] = useState<number>(value);
	const localRef = useRef<number>(local);
	// Resync local state when the parent pushes a new value (e.g. after save/reset).
	useEffect(() => {
		setLocal(value);
		localRef.current = value;
	}, [value]);
	const handleChange = (v: number) => {
		setLocal(v);
		localRef.current = v;
	};
	return (
		<NumberField
			value={local}
			onChange={handleChange}
			onInputChange={() => onCommit(localRef.current)}
			hasError={hasError}
			size={NumberField.SIZE.SMALL}
			width={NumberField.WIDTH.FLEX}
		/>
	);
};

const formatDuration = (minutes: unknown, intl: IntlShape): string =>
	typeof minutes === 'number' && !Number.isNaN(minutes) ? Util.convertMinutesToFullHour(minutes, intl, true) : '';

type CommitDurationFieldProps = {
	value: unknown;
	onCommit: (v: number | null) => void;
	hasError: boolean;
	intl: IntlShape;
};

/**
 * Duration input matching the main app's time-entry field: free-form text
 * ("1h 30m", "90", "1.5h"), parsed and reformatted on blur or Enter, no upper
 * cap. The committed value is integer minutes; unparseable text commits null
 * (field validation surfaces the error) while the raw text stays visible so
 * the user can correct it.
 */
const CommitDurationField = ({value, onCommit, hasError, intl}: CommitDurationFieldProps): JSX.Element => {
	const [local, setLocal] = useState<string>(formatDuration(value, intl));
	const wrapperRef = useRef<HTMLDivElement>(null);
	// Resync local state when the parent pushes a new value (e.g. after save/reset)
	// or the locale changes (re-format the current value).
	useEffect(() => {
		setLocal(formatDuration(value, intl));
	}, [value, intl]);
	return (
		<div
			ref={wrapperRef}
			onKeyDown={(e: React.KeyboardEvent) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					(wrapperRef.current?.querySelector('input') as HTMLInputElement | null)?.blur();
				}
			}}
		>
			<TextField
				value={local}
				onValueChange={setLocal}
				onBlur={() => {
					const minutes = Util.convertTimeInputToMinutes(local, true);
					onCommit(typeof minutes === 'number' ? minutes : null);
					if (typeof minutes === 'number') {
						setLocal(formatDuration(minutes, intl));
					}
				}}
				hasError={hasError}
				size={TextField.SIZE.SMALL}
				width={TextField.WIDTH.FLEX}
			/>
		</div>
	);
};

/**
 * Generic descriptor-driven DS input renderer.
 *
 * Read-only conditions (any one triggers):
 *   - descriptor.type === 'readonly'
 *   - descriptor.editable is false
 *   - readOnly prop is true
 *
 * Commit timing:
 *   - text/number/duration: on blur OR Enter key
 *   - select/multiselect/date/daterange/boolean: immediately on change
 */
export const KeyValueField = ({descriptor, value, onCommit, errorKey, readOnly}: Props): JSX.Element => {
	const ro = readOnly === true || !descriptor.editable || descriptor.type === 'readonly';
	const {options} = useFieldOptions(descriptor);
	const intl = useIntl();

	if (ro) {
		// Boolean fields stay as a (disabled) checkbox in the read-only view rather
		// than rendering "true" / "false" as a string literal.
		if (descriptor.type === 'boolean') {
			return <Checkbox checked={value === true} disabled />;
		}
		// select/multiselect values are raw option ids (e.g. person ids). Resolve them
		// to labels via the descriptor's options — the same source the editable combobox
		// uses — so terminal/read-only cards show names ("Tom Becker"), not bare ids ("20").
		// Unknown ids fall back to the id itself.
		let resolved = descriptor.display;
		if (resolved === undefined && (descriptor.type === 'select' || descriptor.type === 'multiselect')) {
			const labelById = new Map(options.map(o => [o.id, o.label]));
			if (Array.isArray(value)) {
				resolved = value.map(v => labelById.get(String(v)) ?? String(v)).join(', ');
			} else if (value !== null && value !== undefined) {
				resolved = labelById.get(String(value)) ?? String(value);
			}
		}
		if (resolved === undefined && descriptor.type === 'duration') {
			resolved = formatDuration(value, intl) || undefined;
		}
		const display = resolved ?? formatReadOnly(value);
		// size="2" → DS sky/sys/font/body/2 (14px / 20px / 0.15px / weight 350) to match
		// the Figma "Action Box/Write/Dropdown" value-cell typography.
		return <Text size="2">{display}</Text>;
	}

	const hasError = !!errorKey;

	switch (descriptor.type) {
		case 'number': {
			const numVal = typeof value === 'number' ? value : 0;
			return <CommitNumberField value={numVal} onCommit={onCommit as (v: number) => void} hasError={hasError} />;
		}
		case 'duration': {
			return (
				<CommitDurationField
					value={value}
					onCommit={onCommit as (v: number | null) => void}
					hasError={hasError}
					intl={intl}
				/>
			);
		}
		case 'select': {
			// `items` is the dynamic collection; react-aria derives each Item's key
			// from `item.id` automatically. Setting `key` on ComboBox.Item explicitly
			// can break the default `contains` filter (search-as-you-type), so we omit
			// it and let the renderer use `textValue` for filtering.
			//
			// menuTrigger="focus" opens the dropdown as soon as the input is focused;
			// autoFocus moves focus to the input on mount, so clicking "+ Choose X"
			// (which mounts this ComboBox) opens the option list in one step. The
			// built-in `contains` filter then narrows the list as the user types.
			return (
				<ComboBox
					value={value as string}
					onChange={onCommit}
					hasError={hasError}
					items={options as FieldOption[]}
					size={ComboBox.SIZE.SMALL}
					width={ComboBox.WIDTH.FLEX}
					menuTrigger="focus"
					autoFocus
				>
					{(item: FieldOption) => <ComboBox.Item textValue={item.label}>{item.label}</ComboBox.Item>}
				</ComboBox>
			);
		}
		case 'multiselect': {
			const selectedKeys = Array.isArray(value) ? (value as string[]) : [];
			// MultiCombobox needs a `children` render-prop so each selected chip + each
			// menu item renders the option label. Without it the chips display nothing
			// next to the "X" remove button.
			return (
				<MultiCombobox
					items={options.map((o: FieldOption) => ({key: o.id, textValue: o.label}))}
					selectedKeys={selectedKeys}
					setSelectedKeys={onCommit as (keys: string[]) => void}
					height={MultiCombobox.HEIGHT.SMALL}
					width={MultiCombobox.WIDTH.FLEX}
				>
					{item => <span>{item.textValue ?? ''}</span>}
				</MultiCombobox>
			);
		}
		case 'date': {
			return <DatePicker value={value as string} onChange={onCommit as (dateStr: string) => void} size="small" />;
		}
		case 'daterange': {
			const dr = (value as {start?: string; end?: string}) ?? {};
			const handleRangeChange = (range: {startDate?: string; endDate?: string}) =>
				onCommit({start: range?.startDate, end: range?.endDate});
			const formatted = formatDateRange(dr);
			return (
				<DateRangeRow>
					<DateRangeValueText>{formatted ?? '—'}</DateRangeValueText>
					<CompactDateRangeWrapper>
						<DateRangePicker
							startDate={dr.start}
							endDate={dr.end}
							onChange={handleRangeChange}
							size="small"
							width="flex"
						/>
					</CompactDateRangeWrapper>
				</DateRangeRow>
			);
		}
		case 'boolean': {
			return <Checkbox checked={value === true} onChange={onCommit as (checked: boolean) => void} />;
		}
		case 'text':
		default:
			return (
				<CommitTextField
					value={(value as string) ?? ''}
					onCommit={onCommit as (v: string) => void}
					hasError={hasError}
				/>
			);
	}
};
